import { useQuery } from '@apollo/client';
import { isEmpty, kebabCase } from 'lodash';
import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import {
  ConsultType,
  GetAvailableAppointmentHonorificBlockMinuteItemsDocument,
  Honorific,
  HonorificAppointmentBlockMinutes,
} from '../../types/api/graphql';

export type AvailableAppointmentHonorificBlockTimeValuesInput = {
  healthProviderId: string | null | undefined;
  internalAgenda: boolean;
  consultTypes: ConsultType[];
  healthProfessionalId?: string | null | undefined;
  bulkId?: string | null | undefined;
  honorific?: Honorific | null | undefined;
};

const convertHonorificBlockMinuteValues = (
  internalAgenda: boolean,
  items: HonorificAppointmentBlockMinutes[],
  intl: IntlShape
) =>
  items.map(option => ({
    key: option.honorific,
    title: intl.formatMessage({
      id: `common.honorific.${kebabCase(option.honorific)}`,
    }),

    // TODO HAS AGENDA
    defaultValue: option.defaultMinute,
    snap: option.snap,
    values: option.appointmentBlockMinutes.map(value => ({
      key: value.minute.toString(),
      value: value.minute,
      available: internalAgenda
        ? value.internalAvailable
        : value.patientAvailable, // return based on internal agenda
      irregular: value.irregular,
      title: `${intl.formatMessage(
        {
          id:
            value.minute === 0
              ? 'template.entry.minutes.block'
              : 'template.entry.minutes.minutes-desc',
        },
        { minutes: value.minute }
      )}`,
    })),
  }));

// List block minutes for honorific
export function useAvailableAppointmentHonorificBlockMinuteValues(
  input: AvailableAppointmentHonorificBlockTimeValuesInput,
  skip?: boolean
) {
  const {
    data: availableAppointmentHonorificBlockMinuteItemsData,
    loading: availableAppointmentHonorificBlockMinuteItemsLoading,
  } = useQuery(GetAvailableAppointmentHonorificBlockMinuteItemsDocument, {
    variables: {
      ...input,
      healthProviderId: input.healthProviderId ?? '',
      bulkId: isEmpty(input.bulkId) ? null : input.bulkId,
      healthProfessionalId: isEmpty(input.healthProfessionalId)
        ? null
        : input.healthProfessionalId,
    },
    skip: skip === true || !input.healthProviderId,
    fetchPolicy: 'network-only',
  });

  const intl = useIntl();

  const data: {
    key: Honorific;
    title: string;
    defaultValue: number;
    snap: boolean;
    values: {
      key: string;
      value: number;
      available: boolean;
      irregular: boolean;
      title: string;
    }[];
  }[] = useMemo(() => {
    const allItems =
      availableAppointmentHonorificBlockMinuteItemsData?.availableAppointmentHonorificBlockMinutes ??
      [];

    const filteredItems = input.honorific
      ? allItems.filter(item => item.honorific === input.honorific)
      : allItems;

    return convertHonorificBlockMinuteValues(
      input.internalAgenda,
      filteredItems,
      intl
    );
  }, [
    availableAppointmentHonorificBlockMinuteItemsData?.availableAppointmentHonorificBlockMinutes,
    input.honorific,
    input.internalAgenda,
    intl,
  ]);

  return {
    data,
    loading: availableAppointmentHonorificBlockMinuteItemsLoading,
  };
}
